import React from "react";
import { DEALER } from "@/data/dealership";
import { useT } from "@/i18n/lang";
import { ArrowRight, Phone, Star, CreditCard } from "lucide-react";
import FinancingCTA from "@/components/FinancingCTA";

export default function Hero() {
  const { t } = useT();
  return (
    <section id="top" data-testid="hero" className="relative pt-28 md:pt-36 pb-16 md:pb-24 px-6 md:px-12 lg:px-16 overflow-hidden bg-white">
      <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-6 md:gap-8">
        <div className="col-span-12 lg:col-span-7 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-6 reveal">
            <span className="w-8 h-px bg-[#EB0A1E]" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#EB0A1E]">
              {t("hero.eyebrow")}
            </span>
          </div>

          <h1 className="font-display font-bold text-[#0A0A0A] text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter reveal reveal-delay-1">
            {t("hero.headline1")}<br />
            <span className="text-[#EB0A1E]">{t("hero.headline2")}</span> {t("hero.headline3")}
          </h1>

          <p className="mt-6 max-w-xl text-base md:text-lg text-[#52525B] leading-relaxed reveal reveal-delay-2">
            {t("hero.lede")}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3 reveal reveal-delay-3">
            <a
              href={`tel:${DEALER.phoneRaw}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#EB0A1E] text-white px-7 py-4 font-semibold transition-colors group"
            >
              <Phone className="w-4 h-4" />
              {t("hero.callCta")} {DEALER.phone}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#appointment"
              data-testid="hero-book-btn"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] px-7 py-4 font-semibold transition-colors"
            >
              {t("hero.bookCta")}
            </a>
            <FinancingCTA page="hero" testid="hero-finance-btn">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 text-[#0A0A0A] hover:text-[#EB0A1E] px-2 py-4 font-semibold underline underline-offset-4 decoration-[#EB0A1E] decoration-2 transition-colors"
              >
                <CreditCard className="w-4 h-4" />
                {t("hero.financeCta")}
              </button>
            </FinancingCTA>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 max-w-md reveal reveal-delay-3">
            <Stat n="500+" label={t("hero.statCustomers")} />
            <Stat n="5.0★" label={t("hero.statRating")} />
            <Stat n={t("hero.statYearsNum")} label={t("hero.statYears")} />
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 relative">
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F8F8F9]">
            <img
              src={DEALER.headshot}
              alt={`${DEALER.consultant} - ${DEALER.role}`}
              className="absolute inset-0 w-full h-full object-cover object-top"
              data-testid="hero-portrait"
              loading="eager"
              fetchpriority="high"
            />
            <div className="absolute bottom-6 left-6 right-6 md:left-6 md:right-auto md:max-w-[260px] bg-white border border-[#E4E4E7] p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 bg-green-500 rounded-full pulse-dot" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[#0A0A0A]">
                  {t("hero.availableNow")}
                </span>
              </div>
              <div className="font-display font-bold text-lg text-[#0A0A0A] leading-tight">
                {t("hero.sameDay")}
              </div>
              <div className="text-xs text-[#52525B] mt-1">
                {t("hero.sameDaySub")}
              </div>
            </div>
          </div>

          <div className="absolute -top-4 left-6 md:left-auto md:-top-4 md:right-6 bg-[#EB0A1E] text-white px-4 py-2 flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-3.5 h-3.5 fill-white" />
            ))}
            <span className="ml-1 text-xs font-bold uppercase tracking-wider">{t("hero.topRated")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-display font-bold text-2xl text-[#0A0A0A]">{n}</div>
      <div className="text-xs text-[#52525B] mt-1 leading-tight">{label}</div>
    </div>
  );
}
